import { useState, useMemo } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper,
} from '@mui/material'
import { Speed, Search } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'
import { formatNumber } from '../../utils/format'

const DEMO_DATA = [
  { line: 'Line 1', item: '10001', description: 'HV CHILI BNS 15oz', packSize: '24/15oz', runRate: 180, casesPerEC: 1.00 },
  { line: 'Line 1', item: '10002', description: 'FINEST CHILI BNS 15oz', packSize: '24/15oz', runRate: 175, casesPerEC: 1.00 },
  { line: 'Line 1', item: '10005', description: 'HV CHILI NO BNS 15oz', packSize: '24/15oz', runRate: 180, casesPerEC: 1.00 },
  { line: 'Line 2', item: '10010', description: 'HV CHICKEN NOODLE 15oz', packSize: '24/15oz', runRate: 200, casesPerEC: 0.85 },
  { line: 'Line 2', item: '10011', description: 'HV TOMATO SOUP 10.75oz', packSize: '24/10.75oz', runRate: 220, casesPerEC: 0.72 },
  { line: 'Line 2', item: '10012', description: 'HV CREAM OF MUSHROOM 10.75oz', packSize: '24/10.75oz', runRate: 215, casesPerEC: 0.72 },
  { line: 'Line 3', item: '10020', description: 'HV PORK & BEANS 15oz', packSize: '24/15oz', runRate: 190, casesPerEC: 1.00 },
  { line: 'Line 3', item: '10021', description: 'HV BAKED BEANS 28oz', packSize: '12/28oz', runRate: 140, casesPerEC: 1.87 },
  { line: 'Line 4', item: '10030', description: 'GMI PROGRESSO CHKN 19oz', packSize: '12/19oz', runRate: 160, casesPerEC: 1.27 },
  { line: 'Line 4', item: '10031', description: 'GMI PROGRESSO MINESTRONE 19oz', packSize: '12/19oz', runRate: 155, casesPerEC: 1.27 },
]

export default function ItemRunRates() {
  const [search, setSearch] = useState('')
  const [orderBy, setOrderBy] = useState('item')
  const [order, setOrder] = useState('asc')

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(col)
  }

  const rows = useMemo(() => {
    let filtered = DEMO_DATA.filter((r) =>
      `${r.item} ${r.description} ${r.line}`.toLowerCase().includes(search.toLowerCase()),
    )
    filtered.sort((a, b) => {
      const av = a[orderBy], bv = b[orderBy]
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return order === 'asc' ? cmp : -cmp
    })
    return filtered
  }, [search, orderBy, order])

  const columns = [
    { id: 'line', label: 'Line' },
    { id: 'item', label: 'Item #' },
    { id: 'description', label: 'Description' },
    { id: 'packSize', label: 'Pack Size' },
    { id: 'runRate', label: 'Run Rate (cases/hr)', align: 'right' },
    { id: 'casesPerEC', label: 'Cases per EC', align: 'right' },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Speed color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Item Run Rates</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="SAP Production Speeds" size="small" variant="outlined" />
        <Typography variant="overline" color="text.secondary">
          As of Mar 10, 2026 · v4 · {DEMO_DATA.length} items
        </Typography>
      </Box>

      <TextField
        size="small"
        placeholder="Search items…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 280 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.item} hover>
                <TableCell>{row.line}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.item}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.packSize}</TableCell>
                <TableCell align="right">{formatNumber(row.runRate)}</TableCell>
                <TableCell align="right">{row.casesPerEC.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
